import * as React from "react";
import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import * as Router from "@tanstack/react-router";
import { routeTree } from "../routeTree.gen";
import { Page } from "@/components/page";
import { ButtonLink } from "@/components/button-link";
import { useRouter } from "@tanstack/react-router";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { queryClient } from "./query-client-provider";
import { useAuth } from "@clerk/clerk-react";

// Create a new router instance
export const router = Router.createRouter({
  routeTree,
  context: {
    queryClient,
    // biome-ignore lint/style/noNonNullAssertion: We'll inject this when we render.
    auth: undefined!,
  },
  defaultPendingComponent: PendingComponent,
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  defaultNotFoundComponent: NotFoundComponent,
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    from?: string;
  }
}

export function RouterProvider() {
  const auth = useAuth();
  return <Router.RouterProvider router={router} context={{ auth }} />;
}

function PendingComponent() {
  return (
    <Flex justify="center" align="center" pt="50svh">
      <Loader color="pink" />
    </Flex>
  );
}

type ErrorComponentProps = {
  error: Error;
};

function ErrorComponent({ error }: ErrorComponentProps) {
  const title = error.name ?? "Unexpected Error";
  const message = error.message ?? "An unexpected error has occured.";
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  React.useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return (
    <Box mt="xl">
      <Stack gap="sm">
        <Title>{title}</Title>
        <Text>{message}</Text>
        <Box my="lg">
          <Button
            onClick={() => {
              router.invalidate();
            }}
          >
            Try again
          </Button>
        </Box>
        <Text>
          Please open a support ticket by emailing us at{" "}
          <Anchor inherit href="mailto:support@fapinstructor.com">
            support@fapinstructor.com
          </Anchor>{" "}
          with the above error to help us resolve this issue.
        </Text>
      </Stack>
    </Box>
  );
}

function NotFoundComponent() {
  return (
    <Page title="Page Not Found">
      <Center h="100%">
        <Card withBorder>
          <Stack ta="center" gap="sm">
            <Title>Page Not Found</Title>
            <Text maw={400}>
              It looks like this page doesn't exist. To return to where you were
              previously please click the below button.
            </Text>
            <Center>
              <Group>
                <ButtonLink to="/">Go Home</ButtonLink>
              </Group>
            </Center>
          </Stack>
        </Card>
      </Center>
    </Page>
  );
}
